import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import { Input, Col, Row, Dropdown, Menu, Button as AntButton } from 'antd'
import styles from './index.module.scss'
import { useSelector, useDispatch } from 'react-redux'
import * as fromActions from 'store/actions/participants.action'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faPlus, faEllipsisV } from '@fortawesome/pro-solid-svg-icons'
import Button from 'design-system/button'
import { ButtonSizes, ButtonVariant } from 'design-system/button/src/Button'
import { IMainState } from 'store/states'
import { IAreaModel } from 'models/area.model'
import UserAvatar from 'components/ui/user-avatar/UserAvatar'
import { RankmiTable } from '@rankmi/rankmi-table'
import { ListOriginEnum } from 'enum/listOrigin.enum'
import { useCallback } from 'react'
import { canSkipOnRowSelected } from 'helpers'

interface IAddParticipantsModal {
  show: boolean
  onClose: any
  originalListConfig: any
  resetFilters: any
}
const MIN_SEARCH_LENGTH = 3

const AddParticipantsModal: React.FC<IAddParticipantsModal> = ({
  show,
  onClose,
  originalListConfig,
  resetFilters,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const auxSelectedRows = useRef([])
  const [search, setSearch] = useState('')
  const [onSearch, setOnSearch] = useState('')
  const [selectedRows, setSelectedRows] = useState([] as any)
  const [unselectedRows, setUnselectedRows] = useState([] as any)
  const [sort, setSort] = useState({})
  const [page, setPage] = useState(1)
  const [dataSource, setDatasource] = useState([] as any)
  const [allSelected, setAllSelected] = useState(false)
  const [translations, setTranslations] = useState({})
  const {
    inactiveParticipantsList,
    inactiveParticipantsTotal,
    loading,
    inactiveParticipantsListLoading,
  } = useSelector(({ participants }: IMainState) => participants)
  const prefix = 'configuration.users.enableParticipantsModal'

  const generateTableLocale = useCallback(() => {
    const locale = { header: {}, table: {} } as any
    const headers = [
      'addButton',
      'rowsCount',
      'rowsCount_plural',
      'selectedRows',
      'selectedRows_plural',
      'selectAll',
      'clearSelection',
      'search',
      'actions',
    ]
    headers.map((header) => {
      locale.header[header] = t(`${prefix}.table.headers.${header}`)
    })
    const table = ['emptyMessage']
    table.map((tableLocale) => {
      locale.table[tableLocale] = t(`${prefix}.table.table.${tableLocale}`)
    })
    return locale
  }, [t])

  useEffect(() => {
    setTranslations(() => generateTableLocale())
  }, [generateTableLocale])

  useEffect(() => {
    dispatch({
      type: fromActions.PARTICIPANTS_INACTIVE_PROFILES_LIST,
      payload: { page, sort, searchText: onSearch },
    })
  }, [page, sort, onSearch, dispatch])

  const handleActivateUsers = useCallback(
    (ids: number[]) => {
      const difference = auxSelectedRows.current.filter(
        (key: number) => !ids.includes(key)
      )
      setSelectedRows(difference)
      dispatch({
        type: fromActions.PARTICIPANTS_SET_ACTIVE,
        payload: {
          origin: ListOriginEnum.AddUsersModal,
          ids,
          listConfig: { page, searchText: onSearch, sort },
          state: true,
          originalListConfig,
          search: onSearch,
          allSelected,
          unselectedRows,
        },
      })
      resetFilters()
    },
    [
      dispatch,
      onSearch,
      originalListConfig,
      page,
      resetFilters,
      sort,
      allSelected,
      unselectedRows,
    ]
  )

  useEffect(() => {
    setDatasource(
      inactiveParticipantsList.map(
        ({ fullName, avatar, identifier, area, position, id }) => ({
          key: id,
          name: { fullName, avatar },
          identifier,
          'area.name': area ? generateAreas([area]) : '',
          position,
          action: (
            <div className={styles.addParticipantButton}>
              <Button
                onClick={() => handleActivateUsers([id])}
                variant={ButtonVariant.Secondary}
                size={ButtonSizes.Small}
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </div>
          ),
        })
      )
    )
  }, [handleActivateUsers, inactiveParticipantsList])

  useEffect(() => {
    let timer: any
    if (search.length >= MIN_SEARCH_LENGTH) {
      timer = setTimeout(() => {
        setOnSearch(search.trim())
      }, 1000)
    } else {
      setOnSearch('')
    }
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    auxSelectedRows.current = selectedRows
  }, [selectedRows])

  const generateAreas = (userAreas: IAreaModel[]) => {
    return userAreas.map(({ name }) => name).join(', ')
  }

  const columns = [
    {
      title: t(`${prefix}.table.headers.name`),
      dataIndex: 'name',
      key: 'name',
      render: ({ avatar, fullName }: any) => (
        <UserAvatar user={{ fullName, avatar }} className={styles.userData} />
      ),
      sorteable: true,
    },
    {
      title: t(`${prefix}.table.headers.identifier`),
      dataIndex: 'identifier',
      key: 'identifier',
      sorteable: true,
    },
    {
      title: t(`${prefix}.table.headers.position`),
      dataIndex: 'position',
      key: 'position',
      sorteable: true,
    },
    {
      title: t(`${prefix}.table.headers.area`),
      dataIndex: 'area.name',
      key: 'area.name',
    },
    { title: '', dataIndex: 'action', key: 'action' },
  ]

  const handleActivateUsersBulk = () => {
    handleActivateUsers(selectedRows)
  }

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  return (
    <Modal
      size="lg"
      show={show}
      onHide={onClose}
      className={styles.modalContainer}
    >
      <Modal.Body className={'pt-0 px-0'}>
        <Row className="position-relative">
          <h1>{t(`${prefix}.title`)}</h1>
          <a className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
          </a>
        </Row>
        <Row>
          <p>{t(`${prefix}.description`)}</p>
        </Row>
        <Row className="position-relative mt-3 mb-2" gutter={10}>
          <Col xs={16}>
            <Input.Search
              placeholder={t(`${prefix}.searchParticipants`)}
              className={styles.search}
              onChange={(e: any) => handleSearch(e.target.value)}
            />
          </Col>
          <Col xs={8} className="d-flex ">
            <Button
              onClick={() => !!selectedRows.length && handleActivateUsersBulk()}
              className={`${styles.actionButton} w-100`}
              disabled={!selectedRows.length}
              size={ButtonSizes.Small}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              {t(`${prefix}.enableSelectedParticipants`)}
            </Button>
            <Dropdown
              className={'d-none'}
              placement="bottomRight"
              overlay={
                <Menu>
                  <Menu.Item key="1" disabled={true}>
                    {t(`${prefix}.actions.addAll`)}
                  </Menu.Item>
                </Menu>
              }
            >
              <AntButton
                className={`${styles.actionButton} ${styles.buttonSecondary} ml-2`}
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </AntButton>
            </Dropdown>
          </Col>
        </Row>
        <Row>
          <RankmiTable
            data={dataSource}
            total={inactiveParticipantsTotal}
            columnHeaders={columns}
            onSortChange={(sortData: any) => setSort(sortData)}
            onRowSelected={({
              selectedRowKeys,
              allSelected,
              unselectedRowKeys,
              dispatcher,
            }: any) => {
              if (canSkipOnRowSelected(dispatcher)) return
              setUnselectedRows(unselectedRowKeys)
              setSelectedRows(selectedRowKeys)
              setAllSelected(allSelected)
            }}
            selectedRows={selectedRows}
            loading={loading || inactiveParticipantsListLoading}
            onPaginationChange={(currentPage: number) => setPage(currentPage)}
            locale={translations}
            simpleSelectAll={true}
          />
        </Row>
      </Modal.Body>
    </Modal>
  )
}

export default AddParticipantsModal
